/**
 * useStepRedirect Hook
 *
 * Single source of truth: /progress endpoint.
 *
 * Decision tree (progress decides, current page doesn't matter):
 * 1. Profile incomplete → signup stage
 * 2. Active request with changes_requested → /corrections
 * 3. Active request (priority: registration > closure > onboarding) → request's stage route
 * 4. Has clinic_id (no active requests) → /clinic-management
 * 5. Nothing → /home
 *
 * The only current-page check is "am I already where I need to be?" to prevent loops.
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProgressWithAutoRefresh } from '../shared/contexts/ProgressContext';
import { getRedirectTarget } from './stepRedirectLogic';

// Re-export so tests and other consumers can import from either file
export { getRedirectTarget } from './stepRedirectLogic';

// Module-level skip flag — avoids WebView History API issues where
// location.state (backed by window.history.state) can persist incorrectly.
let _skipNextRedirect = false;

/**
 * Call before navigate() to skip the next useStepRedirect check on the
 * destination page. Used for in-flow navigation where progress hasn't
 * updated yet (e.g., going back to edit, moving forward after submit).
 */
export function skipNextStepRedirect() {
  _skipNextRedirect = true;
}

export function useStepRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { progress, isLoading, signup } = useProgressWithAutoRefresh();
  const hasCheckedRef = useRef(false);
  const lastProgressRef = useRef(progress);

  useEffect(() => {
    // Dev nav bypass
    if (sessionStorage.getItem('dev_nav_bypass')) {
      return;
    }

    // Skip if navigating within flow (one-time flag)
    if (_skipNextRedirect) {
      _skipNextRedirect = false;
      hasCheckedRef.current = true;
      return;
    }

    // Wait for progress data
    if (isLoading || !progress) {
      return;
    }

    // Re-check if progress changed (new data from refresh)
    if (lastProgressRef.current !== progress) {
      hasCheckedRef.current = false;
      lastProgressRef.current = progress;
    }

    // Only check once per progress update
    if (hasCheckedRef.current) {
      return;
    }

    hasCheckedRef.current = true;

    const currentPath = location.pathname;
    let target = getRedirectTarget(progress, signup, currentPath);
    // Back from Qi Card after its webhook already submitted the request (the usual order): show the
    // confirmation instead of dropping the doctor on the requests list without a word about the payment.
    if (
      target === '/requests' &&
      currentPath === '/payment' &&
      new URLSearchParams(location.search).get('payment_return') === 'true'
    ) {
      target = '/success';
    }
    if (target) {
      navigate(target, { replace: true });
    }

  }, [progress, isLoading, signup, location.pathname, location.search, navigate]);
}
